import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  fullContent: string;
  date: string;
  category: string;
  image: string;
  author: string;
  tags: string[];
}

// 現在は空のダミーデータ
const blogPosts: BlogPost[] = [];

// 例として any[] で定義（実際は型を付けることを推奨）
const relatedPosts: any[] = [];

function BlogDetail() {
  // ✅ 正しいジェネリック構文に修正
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);

  useEffect(() => {
    // パラメータが無い、または数値に変換できない場合は 1 とする
    const postId = Number.parseInt(id ?? "1", 10);
    const foundPost = blogPosts.find((p) => p.id === postId) ?? null;
    setPost(foundPost);
  }, [id]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/4f6a1d7b5058e6da57f4192877d570f4/8397292e65571c64257096a9f31fb3e1.png" 
                  alt="合同会社サクラネクサスメディア" 
                  className="h-24"
                />
              </h1>
            </div>

            <nav className="hidden md:flex space-x-8 items-center">
              <a
                href="/"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium"
              >
                ホーム
              </a>
              <a
                href="/#about"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium"
              >
                会社概要
              </a>
              <a
                href="/#services"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium"
              >
                サービス
              </a>
              <a
                href="/#company"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium"
              >
                会社情報
              </a>
              <a href="/blog" className="text-blue-600 font-medium cursor-pointer">
                ブログ
              </a>
              <a
                href="/#contact"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium"
              >
                お問い合わせ
              </a>
            </nav>

            <button className="md:hidden w-6 h-6 flex items-center justify-center cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <section className="py-4 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <a href="/" className="hover:text-blue-600 cursor-pointer">
              ホーム
            </a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/blog" className="hover:text-blue-600 cursor-pointer">
              ブログ
            </a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">記事詳細</span>
          </nav>
        </div>
      </section>

      {/* 記事詳細 / 記事が見つからない場合の表示 */}
      {post ? (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
            <p className="text-gray-500 mb-8">{post.excerpt}</p>
            <article
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.fullContent }}
            />
            {/* ここに関連記事やタグなどを追加できる */}
          </div>
        </section>
      ) : (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <i className="ri-file-search-line text-8xl text-gray-300 mb-6"></i>
            <h2 className="text-3xl font-semibold text-gray-600 mb-4">
              記事が見つかりませんでした
            </h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              お探しの記事は現在準備中か、存在しない可能性があります。<br />
              ブログ一覧ページに戻って他の記事をご覧ください。
            </p>
            <a
              href="/blog"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              ブログ一覧に戻る
            </a>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            お困りのことはございませんか？
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            製造やECサイト運営に関するご相談は、お気軽にお問い合わせください
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
          >
            <i className="ri-mail-line mr-2"></i>
            お問い合わせ
            <i className="ri-arrow-right-circle-line ml-2"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">クイックリンク</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="/" className="hover:text-white transition-colors cursor-pointer">
                    ホーム
                  </a>
                </li>
                <li>
                  <a href="/#about" className="hover:text-white transition-colors cursor-pointer">
                    会社概要
                  </a>
                </li>
                <li>
                  <a href="/#services" className="hover:text-white transition-colors cursor-pointer">
                    サービス
                  </a>
                </li>
                <li>
                  <a href="/blog" className="hover:text-white transition-colors cursor-pointer">
                    ブログ
                  </a>
                </li>
                <li>
                  <a href="/#contact" className="hover:text-white transition-colors cursor-pointer">
                    お問い合わせ
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">会社情報</h4>
              <p className="text-gray-400 mb-4">合同会社サクラネクサスメディア</p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">お問い合わせ</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-1"></i>
                  <span>
                    〒103-0027 東京都中央区日本橋1丁目12-4
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 合同会社サクラネクサスメディア. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default BlogDetail;
